import React, {useState} from 'react';
import {StatCard} from './StatCard';
import {PhasePill} from './PhasePill';
import {useDashboard} from './context';
import {solutionList, splitToBullets} from './scoring';
import {formatLastUpdated} from '../../lib/time';
import type {Domain, FieldCell, Jtbd, V2FieldGroup, V2FieldValue} from './types';

/**
 * From → To Transformation Tracker. Each domain tells a From (current state) → In Progress
 * (prioritized activities) → To (future state + quantified benefits) story. Ported from the prototype
 * (local/chris-v2/docs/index.html), adapted to read portfolio data from useDashboard() context.
 */

// Domain-level (sensitive) fields, split FROM-side vs TO-side per the approved mock-up.
const DT_FROM_LABELS: Record<string, string> = {workforceBaseline: 'Workforce Baseline'};
const DT_BENEFIT_LABELS: Record<string, string> = {
  quantifiedDollarBenefits: 'Quantified Dollar Benefits',
  // Upstream relabelled this 2026-09-02; the two surfaces are kept reading the same.
  fteCapacityReclaimed: 'Productivity Statement'
};
const DT_TO_LABELS: Record<string, string> = {
  roleEvolution: 'Role Evolution Statements',
  capacityRedeployment: 'Capacity Redeployment Plan',
  headcountReshaping: 'Headcount Reshaping',
  skillShiftRequirements: 'Skill Shift Requirements'
};
const DT_TO_OPTIONAL_LABELS: Record<string, string> = {
  hiringProfileChange: 'Hiring Profile Change',
  contractorVendorImpact: 'Contractor / Vendor Impact'
};
// Activity-level fields (non-sensitive, repeat per prioritized activity).
const FROM_LABELS: Record<string, string> = {
  operatingModel: 'Operating Model Summary',
  painPoints: 'Key Pain Points',
  currentTooling: 'Current Tooling',
  baselineMetrics: 'Baseline Metrics',
  manualEffortHrsPerWeek: 'Manual Effort (hrs/wk)'
};
const WHENDONE_LABELS: Record<string, string> = {
  outcomeStatement: 'Outcome Statement',
  targetMetric: 'Target Metric',
  capacityImpactHrsPerWeek: 'Capacity Impact (hrs/wk)'
};
const TO_LABELS: Record<string, string> = {
  futureStateSummary: 'Future State Summary',
  targetMetricsDelta: 'Target Metrics (delta)'
};
const TO_TOOLCHAIN_LABELS: Record<string, string> = {futureToolchain: 'Future Toolchain'};
const TO_TOOLCHAIN_OPTIONAL_LABELS: Record<string, string> = {toolsRetiredReplaced: 'Tools Retired / Replaced'};
const TO_REALIZATION_LABELS: Record<string, string> = {
  benefitRealizationTimeline: 'Benefit Realization Timeline',
  dependencies: 'Dependencies'
};

const BASELINE_STATUS_META: Record<string, {label: string; bg: string; text: string; border: string}> = {
  complete: {label: 'Complete', bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0'},
  in_progress: {label: 'In Progress', bg: '#EEF4FF', text: '#2563EB', border: '#C7D9FF'},
  pending: {label: 'Pending', bg: '#F8FAFC', text: '#94A3B8', border: '#E2E8F0'},
  not_ready: {label: 'Future / Not Started', bg: '#F1F5F9', text: '#475569', border: '#CBD5E1'}
};

/** Loose view of a nested field group so label maps can index it by key. */
type CellMap = Record<string, FieldCell | undefined>;
const cellsOf = (o: unknown): CellMap => (o ?? {}) as CellMap;

/**
 * Renders a single Answered/Deferred/N/A field. Pass bulletable to split multi-clause prose into a
 * bullet list (display-only — see splitToBullets). Scoped to domain-level fields; activity-level
 * fields keep plain paragraphs since some prose doesn't split cleanly on the heuristic.
 */
function FieldRow({label, field, bulletable}: Readonly<{label: string; field?: FieldCell; bulletable?: boolean}>): React.ReactElement | null {
  if (!field) return null;
  const st = field.status;
  const stMeta = ({
    Answered: {color: '#16A34A', tag: null as string | null},
    Deferred: {color: '#D97706', tag: 'Deferred'},
    'N/A': {color: '#94A3B8', tag: 'N/A'}
  } as Record<string, {color: string; tag: string | null}>)[st] || {color: '#94A3B8', tag: st};
  const body = st === 'Answered' ? field.value || '—' : field.reason || field.value || '—';
  const bullets = bulletable ? splitToBullets(body) : [body];
  const textColor = st === 'Answered' ? '#334155' : '#94A3B8';
  const textStyle = st === 'Answered' ? 'normal' : 'italic';
  return (
    <div style={{marginBottom: 9}}>
      <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2}}>
        <span style={{fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em'}}>{label}</span>
        {stMeta.tag && (
          <span style={{fontSize: 8, fontWeight: 700, color: stMeta.color, background: '#fff', border: `1px solid ${stMeta.color}40`, borderRadius: 3, padding: '0 4px'}}>
            {stMeta.tag}
          </span>
        )}
      </div>
      {bullets.length > 1 ? (
        <ul style={{margin: 0, paddingLeft: 14, fontSize: 11, color: textColor, lineHeight: 1.6, fontStyle: textStyle}}>
          {bullets.map((b, i) => (
            <li key={i} style={{marginBottom: 2}}>
              {b}
            </li>
          ))}
        </ul>
      ) : (
        <div style={{fontSize: 11, color: textColor, lineHeight: 1.5, fontStyle: textStyle}}>{body}</div>
      )}
    </div>
  );
}

function FieldGroupLabel({children, accent, keyBadge, first}: Readonly<{children: React.ReactNode; accent: string; keyBadge?: boolean; first?: boolean}>): React.ReactElement {
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: 6, marginTop: first ? 0 : 14, marginBottom: 8}}>
      <span style={{fontSize: 10, fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: '0.08em'}}>{children}</span>
      {keyBadge && <span style={{fontSize: 8, fontWeight: 700, color: '#fff', background: accent, borderRadius: 3, padding: '1px 5px'}}>KEY</span>}
    </div>
  );
}

// ─── v2 (Specialized Teams) renderer ─────────────────────────────────────────
//
// The v2 skill emits answers directly rather than in {status, value} cells, and its field set is open
// — leaders carry keys the standard doesn't define. So this half of the view renders BY SHAPE and
// labels by key name, where the cell half above renders a fixed field list. Ported from the share
// prototype (local/chris-v3/index.html) to keep the two surfaces rendering identically.

const V2_LABELS: Record<string, string> = {
  fteCount: 'FTE Count',
  contractorCount: 'Contractor Count',
  spanOfControl: 'Span of Control',
  fteFilledCount: 'FTE Filled',
  openRequisitions: 'Open Requisitions',
  openRequisitionDetail: 'Open Requisition Detail',
  // `note` is the freeform staffing-context field in the stakeholder's renderer; `staffingNote` is
  // the same field under the name used while it was being specced. Both label, either is accepted.
  note: 'Note',
  staffingNote: 'Staffing Note',
  geographicDistribution: 'Geographic Distribution',
  roleComposition: 'Role Composition',
  roleMix: 'Role Mix',
  newCapabilities: 'New Capabilities',
  intake: 'Intake',
  prioritization: 'Prioritization',
  cycleTime: 'Cycle Time',
  timeAllocation: 'Time Allocation',
  workflowChanges: 'Workflow Changes',
  aiRelationship: 'AI Relationship',
  annualRunRate: 'Annual Run Rate',
  capacityConstraints: 'Capacity Constraints',
  painPoints: 'Pain Points',
  capacityPicture: 'Capacity Picture',
  goodLooksLike: 'What Good Looks Like',
  // `level` carries Yes | Some | No rather than a magnitude. Labelled to match the wording in the
  // stakeholder's own renderer (Chris-Thorn/Claude-MD) so the two surfaces read the same.
  level: 'AI Engagement Level',
  toolsAndUses: 'Tools & Uses',
  readinessNote: 'Readiness Note',
  primaryMetric: 'Primary Metric',
  currentValue: 'Current Value',
  volume: 'Volume',
  costView: 'Cost View',
  pain: 'Pain Point',
  targetValue: 'Target Value',
  workflowChange: 'Workflow Change',
  requiredInvestment: 'Required Investment',
  dependencies: 'Dependencies'
};
/** Unknown keys fall back to a humanized key name, so a new field renders without a code change. */
const v2Label = (k: string): string =>
  V2_LABELS[k] ||
  k
    .replaceAll(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();

/**
 * Leaf-to-text for a v2 value. A bare `String()` on this union can print "[object Object]", which is
 * the exact failure this renderer exists to avoid, so every text conversion routes through here:
 * arrays join, and an object renders as its labelled fields.
 */
function v2Text(value: V2FieldValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(v => v2Text(v)).join(', ');
  return Object.entries(value)
    .map(([k, v]) => `${v2Label(k)}: ${v2Text(v)}`)
    .join(' · ');
}

/** Stable key for a roster entry — derived from content, since an array index is not a valid key. */
const v2EntryKey = (o: Record<string, V2FieldValue>): string =>
  Object.entries(o)
    .map(([k, v]) => `${k}=${v2Text(v)}`)
    .join('|');

/** Bare numbers on these keys read as an ID rather than money unless formatted. */
const V2_CURRENCY_KEYS = new Set(['annualRunRate']);

// Compensation is not a dashboard field (decided 2026-08-26). A v2 group renders every key it
// carries, so a slice that reintroduced salary would surface it unprompted — dropped at render time
// rather than relying on the uploaded data staying clean.
const V2_OMIT_KEYS = new Set(['partialKnownSalary', 'partialKnownSalaryNote']);

/** Loose view of a v2 group, mirroring `cellsOf` for the cell shape. */
const groupOf = (o: unknown): V2FieldGroup => (o ?? {}) as V2FieldGroup;

/** True when a value carries no content worth a row: nullish, blank string, or empty array/object. */
function v2IsEmpty(v: V2FieldValue): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

function v2Display(fkey: string, value: V2FieldValue): string {
  const text = v2Text(value);
  if (V2_CURRENCY_KEYS.has(fkey)) {
    // The same keys also carry prose ("Deferred", "Roughly flat vs. From state"), which must pass
    // through untouched — so format only when the value is actually numeric.
    let n: number | null = null;
    if (typeof value === 'number') n = value;
    else if (/^\d+(\.\d+)?$/.test(text.trim())) n = Number(text);
    if (n !== null) return n.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
  }
  return text;
}

/**
 * Renders one entry of an object array (e.g. `from.workforce.roleComposition`, a person roster). The
 * first key reads as the entry's headline (a name); the rest render as labelled detail, so the block
 * stays readable without the renderer knowing the field names.
 */
function V2ObjectEntry({obj}: Readonly<{obj: Record<string, V2FieldValue>}>): React.ReactElement | null {
  const keys = Object.keys(obj).filter(k => !v2IsEmpty(obj[k]));
  if (keys.length === 0) return null;
  const [head, ...rest] = keys;
  return (
    <div style={{padding: '4px 0', borderTop: '1px solid rgba(148,163,184,0.22)'}}>
      <div style={{fontSize: 11, fontWeight: 700, color: '#0F172A', lineHeight: 1.4}}>{v2Text(obj[head])}</div>
      {rest.length > 0 && (
        <div style={{fontSize: 10, color: '#64748B', lineHeight: 1.5}}>{rest.map(k => `${v2Label(k)}: ${v2Text(obj[k])}`).join(' · ')}</div>
      )}
    </div>
  );
}

/**
 * Renders a v2 value by shape: prose prints as a paragraph, a string array becomes bullets, an object
 * array becomes entry blocks, and a bare object renders as labelled lines. Without the array branches
 * an object array hits String() and prints "[object Object]" repeated.
 */
function V2Value({fkey, value}: Readonly<{fkey: string; value: V2FieldValue}>): React.ReactElement {
  if (Array.isArray(value)) {
    const objs = value.filter(v => v && typeof v === 'object' && !Array.isArray(v)) as Record<string, V2FieldValue>[];
    if (objs.length === value.length) {
      return (
        <div>
          {objs.map(o => (
            <V2ObjectEntry key={v2EntryKey(o)} obj={o} />
          ))}
        </div>
      );
    }
    return (
      <ul style={{margin: 0, paddingLeft: 14, fontSize: 11, color: '#334155', lineHeight: 1.6}}>
        {value
          .filter(v => !v2IsEmpty(v))
          .map(v => (
            <li key={v2Text(v)} style={{marginBottom: 2}}>
              {v2Text(v)}
            </li>
          ))}
      </ul>
    );
  }
  if (value && typeof value === 'object') {
    return (
      <div>
        {Object.keys(value)
          .filter(k => !v2IsEmpty(value[k]))
          .map(k => (
            <div key={k} style={{fontSize: 11, color: '#334155', lineHeight: 1.5}}>
              <span style={{color: '#64748B'}}>{v2Label(k)}:</span> {v2Text(value[k])}
            </div>
          ))}
      </div>
    );
  }
  return <div style={{fontSize: 11, color: '#334155', lineHeight: 1.5}}>{v2Display(fkey, value)}</div>;
}

function V2Field({fkey, value}: Readonly<{fkey: string; value: V2FieldValue}>): React.ReactElement | null {
  if (v2IsEmpty(value) || V2_OMIT_KEYS.has(fkey)) return null;
  // Multi-entry values get a count in the label so a long roster is legible at a glance.
  const count = Array.isArray(value) ? value.length : null;
  return (
    <div style={{marginBottom: 8}}>
      <div style={{fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2}}>
        {v2Label(fkey)}
        {count ? ` (${count})` : ''}
      </div>
      <V2Value fkey={fkey} value={value} />
    </div>
  );
}

function V2Group({label, obj, accent, first}: Readonly<{label: string; obj?: V2FieldGroup; accent: string; first?: boolean}>): React.ReactElement | null {
  if (!obj || typeof obj !== 'object') return null;
  const keys = Object.keys(obj).filter(k => !v2IsEmpty(obj[k]) && !V2_OMIT_KEYS.has(k));
  if (keys.length === 0) return null;
  return (
    <div>
      <FieldGroupLabel accent={accent} first={first}>
        {label}
      </FieldGroupLabel>
      {keys.map(k => (
        <V2Field key={k} fkey={k} value={obj[k]} />
      ))}
    </div>
  );
}

/** Exported for the v2-shape render test (tests/from-to-v2.test.ts); the view itself renders it on expand. */
export function FromToExpanded({domain}: Readonly<{domain: Domain}>): React.ReactElement {
  const dt = domain.domainTransformation;
  // Presence of both From and To selects the v2 renderer; the two shapes never mix within a domain.
  const dtV2 = !!(dt?.from && dt.to);
  const allPrioritized = domain.jtbds.filter(j => j.prioritized || j.baselineStatus);
  const notReady = allPrioritized.filter(j => (j.baselineStatus || 'pending') === 'not_ready');
  const active = allPrioritized.filter(j => (j.baselineStatus || 'pending') !== 'not_ready');
  const tools = [...new Set(domain.jtbds.flatMap(j => solutionList(j)).filter(Boolean))];
  const dtCells = cellsOf(dt);

  // Shared 3-column template so From | In Progress | To line up vertically down the whole view. Each
  // prioritized activity is its own horizontal band (header + a 3-cell grid row) so one activity reads
  // left-to-right on a single aligned band. Domain-level summary sits in its own overview band on top,
  // with a clickable index that jumps to the activity bands.
  const GRID: React.CSSProperties = {display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: '0 12px', alignItems: 'stretch'};
  const bandStyle: React.CSSProperties = {background: '#fff', border: '1px solid #E8EEF5', borderRadius: 12, padding: '12px 14px', marginBottom: 12};
  const COL = {from: {bg: '#FEF2F2', border: '#FECACA'}, ip: {bg: '#FFFDF5', border: '#FDE68A'}, to: {bg: '#F0FDF4', border: '#BBF7D0'}};
  const cellStyle = (c: {bg: string; border: string}): React.CSSProperties => ({background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: '12px 14px'});
  const emptyStyle: React.CSSProperties = {fontSize: 11, color: '#94A3B8', fontStyle: 'italic'};
  const toolChip: React.CSSProperties = {fontSize: 11, padding: '3px 9px', background: '#fff', border: '1px solid #FDE68A', borderRadius: 6, color: '#92400E', fontWeight: 600};
  const tocNum: React.CSSProperties = {width: 18, height: 18, borderRadius: '50%', background: '#92400E', color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Mono', monospace", flexShrink: 0};
  const actNum: React.CSSProperties = {width: 20, height: 20, borderRadius: '50%', background: '#0F172A', color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Mono', monospace", flexShrink: 0};
  const actId = (n: number): string => `ft-${domain.id}-act-${n}`;
  const jumpTo = (n: number): void => {
    const el = document.getElementById(actId(n));
    if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  const headers = [
    {icon: '⬅', title: 'From', sub: 'Current State', accent: '#DC2626'},
    {icon: '⚡', title: 'In Progress', sub: 'Prioritized Activities Only', accent: '#D97706'},
    {icon: '🎯', title: 'To', sub: 'Future State + Quantified Benefits', accent: '#16A34A'}
  ];

  return (
    <div style={{padding: '18px 18px 20px', borderTop: '1px solid #F1F5F9', background: '#FBFCFE'}}>
      {/* Column headers — same 3-column geometry as every band below, so columns align */}
      <div style={{...bandStyle, background: 'transparent', border: '1px solid transparent', padding: '0 14px', marginBottom: 6}}>
        <div style={GRID}>
          {headers.map(h => (
            <div key={h.title} style={{display: 'flex', alignItems: 'center', gap: 10, padding: '2px 4px'}}>
              <div style={{width: 30, height: 30, borderRadius: 8, background: h.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#fff', flexShrink: 0}}>{h.icon}</div>
              <div>
                <div style={{fontSize: 12, fontWeight: 800, color: h.accent, textTransform: 'uppercase', letterSpacing: '0.08em'}}>{h.title}</div>
                <div style={{fontSize: 10, color: '#94A3B8'}}>{h.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── OVERVIEW BAND (domain-level summary) ── */}
      <div style={bandStyle}>
        <div style={{fontSize: 10, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, paddingLeft: 2}}>
          Domain-Level · How the Team Transforms
        </div>
        {/* The narrative is the leader's own framing of the whole From → To arc, so it spans all three
            columns above the grid rather than sitting inside any one of them. */}
        {dt?.transformationNarrative && (
          <div style={{borderLeft: '3px solid #CBD5E1', background: '#F8FAFC', borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: 12}}>
            <div style={{fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4}}>Transformation Narrative</div>
            <div style={{fontSize: 12, color: '#334155', lineHeight: 1.6}}>{dt.transformationNarrative}</div>
          </div>
        )}
        <div style={GRID}>
          {/* FROM */}
          <div style={cellStyle(COL.from)}>
            {dtV2 ? (
              <>
                <V2Group label="Workforce" obj={dt?.from?.workforce} accent="#DC2626" first />
                <V2Group label="Operating Model" obj={dt?.from?.operatingModel} accent="#DC2626" />
                <V2Group label="Cost & Capacity" obj={dt?.from?.costCapacity} accent="#DC2626" />
                <V2Group label="Current AI Engagement" obj={dt?.from?.currentAIEngagement} accent="#DC2626" />
              </>
            ) : (
              <>
                <FieldGroupLabel accent="#DC2626" first>
                  Workforce Baseline
                </FieldGroupLabel>
                {dt && dt.workforceBaseline ? <FieldRow label={DT_FROM_LABELS.workforceBaseline} field={dt.workforceBaseline} bulletable /> : <div style={emptyStyle}>Not captured yet.</div>}
              </>
            )}
          </div>
          {/* IN PROGRESS */}
          <div style={cellStyle(COL.ip)}>
            <FieldGroupLabel accent="#D97706" keyBadge first>
              Tools Being Deployed
            </FieldGroupLabel>
            {tools.length > 0 ? (
              <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                {tools.map(t => (
                  <span key={t} style={toolChip}>
                    {t}
                  </span>
                ))}
              </div>
            ) : (
              <div style={emptyStyle}>No tools identified yet.</div>
            )}
            {active.length > 0 && <FieldGroupLabel accent="#D97706">{`In Progress (${active.length})`}</FieldGroupLabel>}
            {active.length > 0 && (
              <div style={{display: 'flex', flexDirection: 'column', gap: 3}}>
                {active.map((j, i) => (
                  <div
                    key={i}
                    onClick={() => jumpTo(i + 1)}
                    style={{display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: '#fff', border: '1px solid #FDE68A', borderRadius: 6, cursor: 'pointer', transition: 'background .12s, border-color .12s'}}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = '#FEF9C3';
                      e.currentTarget.style.borderColor = '#F59E0B';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.borderColor = '#FDE68A';
                    }}
                  >
                    <span style={tocNum}>{i + 1}</span>
                    <span style={{fontSize: 11, fontWeight: 600, color: '#92400E', lineHeight: 1.3}}>{j.name}</span>
                    {j.needsFollowUp && <span style={{marginLeft: 'auto', color: '#B45309', fontSize: 11}}>⚑</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* TO */}
          <div style={cellStyle(COL.to)}>
            {dtV2 ? (
              <>
                <V2Group label="Workforce" obj={dt?.to?.workforce} accent="#16A34A" first />
                <V2Group label="Operating Model" obj={dt?.to?.operatingModel} accent="#16A34A" />
                <V2Group label="Cost & Capacity" obj={dt?.to?.costCapacity} accent="#16A34A" />
              </>
            ) : (
              <>
                <FieldGroupLabel accent="#16A34A" first>
                  Quantified Benefits — Performance
                </FieldGroupLabel>
                {Object.keys(DT_BENEFIT_LABELS).map(k => (
                  <FieldRow key={k} label={DT_BENEFIT_LABELS[k]} field={dtCells[k]} bulletable />
                ))}
                <FieldGroupLabel accent="#16A34A" keyBadge>
                  Workforce Impact
                </FieldGroupLabel>
                {Object.keys(DT_TO_LABELS).map(k => (
                  <FieldRow key={k} label={DT_TO_LABELS[k]} field={dtCells[k]} bulletable />
                ))}
                {Object.keys(DT_TO_OPTIONAL_LABELS).map(k => (dtCells[k] ? <FieldRow key={k} label={DT_TO_OPTIONAL_LABELS[k]} field={dtCells[k]} bulletable /> : null))}
                {!dt && <div style={emptyStyle}>Not captured yet.</div>}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── PER-ACTIVITY BANDS ── */}
      {active.length === 0 && <div style={{...emptyStyle, fontSize: 12, padding: '2px 2px 12px'}}>No prioritized activities baselined yet.</div>}
      {active.map((j, i) => {
        const ftb = j.fromToBaseline;
        const bs = j.baselineStatus || 'pending';
        const bsMeta = BASELINE_STATUS_META[bs] || BASELINE_STATUS_META.pending;
        const fromCells = cellsOf(ftb?.from);
        const whenDoneCells = cellsOf(ftb?.whenDone);
        const toCells = cellsOf(ftb?.to);
        // A v2 activity baseline carries its answers as bare values under the leader's own keys, so a
        // plain-string member is what distinguishes it from the cell shape ({status, value} objects).
        const fromGroup = groupOf(ftb?.from);
        const toGroup = groupOf(ftb?.to);
        const ftbV2 = Object.values(fromGroup).some(v => typeof v === 'string');

        let fromCellContent: React.ReactNode;
        if (ftbV2) fromCellContent = Object.keys(fromGroup).map(k => <V2Field key={k} fkey={k} value={fromGroup[k]} />);
        else if (ftb?.from) fromCellContent = Object.keys(FROM_LABELS).map(k => <FieldRow key={k} label={FROM_LABELS[k]} field={fromCells[k]} />);
        else fromCellContent = <div style={emptyStyle}>No baseline captured yet.</div>;

        let toCellContent: React.ReactNode;
        if (ftbV2) toCellContent = Object.keys(toGroup).map(k => <V2Field key={k} fkey={k} value={toGroup[k]} />);
        else if (ftb?.to)
          toCellContent = (
            <>
              {Object.keys(TO_LABELS).map(k => (
                <FieldRow key={k} label={TO_LABELS[k]} field={toCells[k]} />
              ))}
              {Object.keys(TO_TOOLCHAIN_LABELS).map(k => (
                <FieldRow key={k} label={TO_TOOLCHAIN_LABELS[k]} field={toCells[k]} />
              ))}
              {Object.keys(TO_TOOLCHAIN_OPTIONAL_LABELS).map(k => (toCells[k] ? <FieldRow key={k} label={TO_TOOLCHAIN_OPTIONAL_LABELS[k]} field={toCells[k]} /> : null))}
              {Object.keys(TO_REALIZATION_LABELS).map(k => (
                <FieldRow key={k} label={TO_REALIZATION_LABELS[k]} field={toCells[k]} />
              ))}
            </>
          );
        else toCellContent = <div style={emptyStyle}>No future-state detail captured yet.</div>;

        return (
          <div key={i} id={actId(i + 1)} style={{...bandStyle, scrollMarginTop: 14}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10, paddingLeft: 2}}>
              <span style={actNum}>{i + 1}</span>
              <PhasePill phase={j.phase} small />
              <span style={{fontSize: 13, fontWeight: 700, color: '#0F172A'}}>{j.name}</span>
              <span style={{fontSize: 9, fontWeight: 700, color: bsMeta.text, background: bsMeta.bg, border: `1px solid ${bsMeta.border}`, borderRadius: 4, padding: '1px 6px'}}>{bsMeta.label}</span>
              {ftb && ftb.lastUpdated && <span style={{fontSize: 9, color: '#94A3B8', fontFamily: "'DM Mono', monospace", marginLeft: 'auto'}}>Updated {formatLastUpdated(ftb.lastUpdated)}</span>}
            </div>
            {j.needsFollowUp && (
              <div style={{display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, fontSize: 10, fontWeight: 700, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '5px 9px'}}>
                <span style={{fontSize: 11}}>⚑</span> Deferred — needs Domain/SA leader follow-up
              </div>
            )}
            <div style={GRID}>
              {/* FROM */}
              <div style={cellStyle(COL.from)}>
                {fromCellContent}
              </div>
              {/* IN PROGRESS */}
              <div style={cellStyle(COL.ip)}>
                <div style={{display: 'grid', gap: 4, marginBottom: ftb && ftb.whenDone ? 8 : 0}}>
                  <div style={{fontSize: 11, color: '#475569'}}>
                    <b style={{color: '#92400E'}}>Solution:</b> {solutionList(j).join(', ') || '—'}
                  </div>
                  <div style={{fontSize: 11, color: '#475569'}}>
                    <b style={{color: '#92400E'}}>Next Step:</b> {j.nextStep || 'Not yet defined'}
                  </div>
                  <div style={{fontSize: 11, color: '#475569'}}>
                    <b style={{color: '#92400E'}}>Timing:</b> {j.nextStepTiming || 'Not yet defined'}
                  </div>
                  {j.blocker && (
                    <div style={{fontSize: 11, color: '#DC2626'}}>
                      <b>Blocker:</b> {j.blocker}
                    </div>
                  )}
                </div>
                {ftb && ftb.whenDone && (
                  <div style={{background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 10px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6}}>
                      <span style={{fontSize: 9, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.08em'}}>When Done</span>
                      <span style={{fontSize: 8, fontWeight: 700, color: '#fff', background: '#D97706', borderRadius: 3, padding: '1px 5px'}}>KEY</span>
                    </div>
                    {Object.keys(WHENDONE_LABELS).map(k => (
                      <FieldRow key={k} label={WHENDONE_LABELS[k]} field={whenDoneCells[k]} />
                    ))}
                  </div>
                )}
              </div>
              {/* TO */}
              <div style={cellStyle(COL.to)}>
                {toCellContent}
              </div>
            </div>
          </div>
        );
      })}

      {/* Future / Not Started — below the bands */}
      {notReady.map(j => (
        <div key={j.id ?? j.name} style={{marginBottom: 12, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '12px 16px'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
            <span style={{fontSize: 12, fontWeight: 700, color: '#0F172A'}}>{j.name}</span>
            <span style={{fontSize: 10, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em'}}>Future / Not Started</span>
          </div>
          <div style={{fontSize: 11, color: '#475569', lineHeight: 1.5}}>{j.notReadyReason || 'On the roadmap — not started yet.'}</div>
        </div>
      ))}

      {!dt && allPrioritized.length === 0 && <div style={{fontSize: 12, color: '#94A3B8', fontStyle: 'italic'}}>No transformation baseline loaded for this domain yet.</div>}
      {dt && dt.lastUpdated && (
        <div style={{fontSize: 9, color: '#94A3B8', fontFamily: "'DM Mono', monospace", marginTop: 2}}>Domain-level snapshot updated {formatLastUpdated(dt.lastUpdated)}</div>
      )}
    </div>
  );
}

function FromToCard({domain, expanded, onToggle}: Readonly<{domain: Domain; expanded: boolean; onToggle: () => void}>): React.ReactElement {
  const {serviceAreas} = useDashboard();
  const dt = domain.domainTransformation;
  const prioritized = domain.jtbds.filter(j => j.prioritized || j.baselineStatus);
  const hasData = !!dt || prioritized.length > 0;
  const total = domain.jtbds.length;
  // `not_ready` means the activity is on the roadmap but not started, so it is reported separately
  // from the work actually under way — the bare prioritized count read as progress that did not exist.
  const notReady = prioritized.filter(j => (j.baselineStatus || 'pending') === 'not_ready');
  const activeCount = prioritized.length - notReady.length;
  const completeCount = prioritized.filter(j => (j.baselineStatus || 'pending') === 'complete').length;
  const progressPct = prioritized.length ? Math.round((completeCount / prioritized.length) * 100) : 0;
  const tools = [...new Set(domain.jtbds.flatMap(j => solutionList(j)).filter(Boolean))];
  const saColor = serviceAreas.find(s => s.id === domain.area)?.color || '#94A3B8';

  const ringR = 15;
  const ringC = 2 * Math.PI * ringR;

  return (
    <div style={{background: '#fff', borderRadius: 14, border: `2px solid ${hasData ? '#3B82F6' : '#E2E8F0'}`, boxShadow: hasData ? '0 8px 32px rgba(59,130,246,0.08)' : 'none', overflow: 'hidden', marginBottom: 20, opacity: hasData ? 1 : 0.7}}>
      <div onClick={() => hasData && onToggle()} style={{padding: '16px 22px', cursor: hasData ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'}}>
        <div style={{width: 4, height: 40, borderRadius: 2, background: saColor, flexShrink: 0}} />
        <div style={{flex: 1, minWidth: 160}}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <span style={{fontSize: 16, fontWeight: 800, color: '#0F172A'}}>{domain.name}</span>
            {!hasData && <span style={{fontSize: 9, fontWeight: 700, color: '#94A3B8', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 4, padding: '1px 6px'}}>Not yet baselined</span>}
          </div>
          <div style={{fontSize: 12, color: '#64748B', marginTop: 2}}>
            {domain.leader} · {domain.area}
          </div>
        </div>

        {hasData && (
          <>
            <div style={{textAlign: 'center', minWidth: 104}}>
              <div style={{fontSize: 18, fontWeight: 800, color: '#334155', fontFamily: "'DM Mono', monospace"}}>
                {prioritized.length} <span style={{fontSize: 13, fontWeight: 600, color: '#94A3B8'}}>of {total}</span>
              </div>
              <div style={{fontSize: 8, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em'}}>prioritized</div>
              {prioritized.length > 0 && (
                <div style={{fontSize: 9, color: '#64748B', marginTop: 2}}>
                  {activeCount} in progress
                  {notReady.length > 0 ? ` \u00b7 ${notReady.length} future` : ''}
                </div>
              )}
            </div>
            <div style={{textAlign: 'center', minWidth: 40, position: 'relative'}}>
              <svg width="36" height="36" style={{transform: 'rotate(-90deg)'}}>
                <circle cx="18" cy="18" r={ringR} fill="none" stroke="#E2E8F0" strokeWidth="3" />
                <circle cx="18" cy="18" r={ringR} fill="none" stroke="#3B82F6" strokeWidth="3" strokeDasharray={ringC} strokeDashoffset={ringC * (1 - progressPct / 100)} strokeLinecap="round" />
              </svg>
              <div style={{position: 'absolute', top: 11, left: 0, width: 36, textAlign: 'center', fontSize: 8, fontWeight: 800, color: '#3B82F6'}}>{progressPct}%</div>
              <div style={{fontSize: 8, color: '#94A3B8', marginTop: 2}}>progress</div>
            </div>
            <div style={{fontSize: 10, color: '#94A3B8', maxWidth: 130}}>{tools.length > 0 ? tools.slice(0, 3).join(', ') : 'No tools yet'}</div>
            <button style={{padding: '6px 12px', background: expanded ? '#3B82F6' : '#94A3B8', borderRadius: 6, fontSize: 11, fontWeight: 700, color: '#fff', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap'}}>{expanded ? 'Hide ▴' : 'View Detail ▸'}</button>
          </>
        )}
      </div>
      {expanded && hasData && <FromToExpanded domain={domain} />}
    </div>
  );
}

/** From → To Transformation Tracker view. */
export function FromToView(): React.ReactElement {
  const {domains, serviceAreas} = useDashboard();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterArea, setFilterArea] = useState('All');

  const hasBaseline = (d: Domain): boolean => !!d.domainTransformation || d.jtbds.some(j => j.prioritized || j.baselineStatus);
  const shown = domains.filter(d => filterArea === 'All' || d.area === filterArea);
  const withData = shown.filter(hasBaseline);
  const totalPrioritized = domains.flatMap(d => d.jtbds).filter(j => j.prioritized || j.baselineStatus).length;

  // sort: data-bearing domains first, then alpha
  const sorted = [...shown].sort((a, b) => {
    const ad = hasBaseline(a) ? 0 : 1;
    const bd = hasBaseline(b) ? 0 : 1;
    if (ad !== bd) return ad - bd;
    return a.name.localeCompare(b.name);
  });

  const areaOptions = ['All', ...serviceAreas.map(a => a.id)];
  const countBaselineStatus = (status: Jtbd['baselineStatus']): number => domains.flatMap(d => d.jtbds).filter(j => j.baselineStatus === status).length;

  return (
    <div>
      <div style={{marginBottom: 20}}>
        <h2 style={{fontSize: 20, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.02em', marginBottom: 4}}>From → To Transformation Tracker</h2>
        <p style={{fontSize: 13, color: '#64748B'}}>
          Each domain tells a From → In Progress → To story · {withData.length} domain{withData.length === 1 ? '' : 's'} baselined · {totalPrioritized} prioritized activities
        </p>
      </div>

      <div style={{background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '14px 20px', marginBottom: 24, maxWidth: 800}}>
        <div style={{fontSize: 11, fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8}}>How the columns connect</div>
        <div style={{fontSize: 12, color: '#475569', lineHeight: 1.7}}>
          <b style={{color: '#0F172A'}}>From → In Progress:</b> Each baseline metric in From has a corresponding target on the activity card working to move it.
          <br />
          <b style={{color: '#0F172A'}}>In Progress → To:</b> Each activity's "When Done" benefit rolls up into To's aggregated benefits and workforce impact.
          <br />
          <b style={{color: '#0F172A'}}>From → To:</b> Every baseline metric has a corresponding target. The delta is the quantified benefit.
        </div>
      </div>

      <div style={{display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap'}}>
        <StatCard label="Domains Baselined" value={withData.length} sub={`of ${shown.length} shown`} accent="#3B82F6" />
        <StatCard label="Prioritized Activities" value={totalPrioritized} sub="in active management" accent="#8B5CF6" />
        <StatCard label="Complete" value={countBaselineStatus('complete')} sub="baseline captured" accent="#16A34A" />
        <StatCard label="In Progress" value={countBaselineStatus('in_progress')} sub="deferred fields remain" accent="#2563EB" />
        <StatCard label="Future / Not Started" value={countBaselineStatus('not_ready')} sub="roadmap / not started" accent="#475569" />
      </div>

      {/* SA filter */}
      <div style={{display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap'}}>
        <span style={{fontSize: 11, color: '#94A3B8', fontFamily: "'DM Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em'}}>SA</span>
        {areaOptions.map(a => {
          const active = filterArea === a;
          const c = a === 'All' ? '#64748B' : serviceAreas.find(s => s.id === a)?.color || '#64748B';
          return (
            <button
              key={a}
              onClick={() => {
                setFilterArea(a);
                setExpandedId(null);
              }}
              style={{padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, background: active ? c + '15' : '#fff', border: `1.5px solid ${active ? c : '#E2E8F0'}`, color: active ? c : '#64748B'}}
            >
              {a}
            </button>
          );
        })}
      </div>

      {sorted.map(d => {
        const id = String(d.id ?? d.name);
        return <FromToCard key={id} domain={d} expanded={expandedId === id} onToggle={() => setExpandedId(expandedId === id ? null : id)} />;
      })}
    </div>
  );
}
